export type StudioImportableMediaType =
  | "image"
  | "document"
  | "video"
  | "animation"
  | "simulation";

export const MAX_EMBEDDED_STUDIO_ASSET_BYTES = 2_500_000;

export type StudioMediaImportSpec = {
  label: string;
  accept: string;
  extensions: string[];
  mimeTypes: string[];
  fallbackMimeType: string;
};

export const studioMediaImportSpecs: Record<
  StudioImportableMediaType,
  StudioMediaImportSpec
> = {
  image: {
    label: "Importer une image",
    accept: ".png,.jpg,.jpeg,.webp,.gif,.svg,image/png,image/jpeg,image/webp,image/gif,image/svg+xml",
    extensions: ["png", "jpg", "jpeg", "webp", "gif", "svg"],
    mimeTypes: [
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/gif",
      "image/svg+xml",
    ],
    fallbackMimeType: "image/png",
  },
  document: {
    label: "Importer un document PDF",
    accept: ".pdf,application/pdf",
    extensions: ["pdf"],
    mimeTypes: ["application/pdf"],
    fallbackMimeType: "application/pdf",
  },
  video: {
    label: "Importer une vidéo",
    accept: ".mp4,.webm,video/mp4,video/webm",
    extensions: ["mp4", "webm"],
    mimeTypes: ["video/mp4", "video/webm"],
    fallbackMimeType: "video/mp4",
  },
  animation: {
    label: "Importer une animation",
    accept: ".html,.htm,text/html",
    extensions: ["html", "htm"],
    mimeTypes: ["text/html"],
    fallbackMimeType: "text/html",
  },
  simulation: {
    label: "Importer une simulation",
    accept: ".html,.htm,text/html",
    extensions: ["html", "htm"],
    mimeTypes: ["text/html"],
    fallbackMimeType: "text/html",
  },
};

export function studioMediaImportSpec(type: string) {
  return studioMediaImportSpecs[type as StudioImportableMediaType];
}

export function studioAssetExtension(name: string) {
  return name.split(".").pop()?.toLowerCase() ?? "";
}

export function validateStudioMediaFile(
  type: StudioImportableMediaType,
  file: { name: string; type: string; size: number },
) {
  const spec = studioMediaImportSpecs[type];
  if (file.size > MAX_EMBEDDED_STUDIO_ASSET_BYTES)
    return `Fichier trop volumineux : ${Math.ceil(file.size / 1_000_000)} Mo. Pour garder un JSON portable et une sauvegarde locale fiable, la limite est de 2,5 Mo.`;
  const extension = studioAssetExtension(file.name);
  if (!spec.extensions.includes(extension))
    return `Format non pris en charge pour ce bloc. Formats autorisés : ${spec.extensions.join(", ")}.`;
  if (file.type && !spec.mimeTypes.includes(file.type))
    return `Type de fichier incompatible : ${file.type}.`;
  return "";
}

export function resolvedStudioMediaMimeType(
  type: StudioImportableMediaType,
  file: { name: string; type: string },
) {
  if (file.type && studioMediaImportSpecs[type].mimeTypes.includes(file.type))
    return file.type;
  const ext = studioAssetExtension(file.name);
  const byExtension: Record<string, string> = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    gif: "image/gif",
    svg: "image/svg+xml",
    pdf: "application/pdf",
    mp4: "video/mp4",
    webm: "video/webm",
    html: "text/html",
    htm: "text/html",
  };
  return byExtension[ext] ?? studioMediaImportSpecs[type].fallbackMimeType;
}
